import React, { useMemo, useState } from 'react';
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Selection, Sort } from '@syncfusion/ej2-react-grids';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import type { RowSelectingEventArgs } from '@syncfusion/ej2-react-grids';
import { ExternalSourceCandidate } from './types';

interface ExternalDataSourcePickerProps {
    visible: boolean;
    sources: ExternalSourceCandidate[];
    linkedSourceIds: string[];
    onAdd: (sources: ExternalSourceCandidate[]) => void;
    onClose: () => void;
}

const ExternalDataSourcePicker: React.FC<ExternalDataSourcePickerProps> = ({
    visible,
    sources,
    linkedSourceIds,
    onAdd,
    onClose,
}) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const linkedSet = useMemo(() => new Set(linkedSourceIds), [linkedSourceIds]);
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const addDisabled = selectedIds.length === 0;

    const addSelected = () => {
        const selectedSources = sources.filter(source => selectedSet.has(source.ID));
        onAdd(selectedSources);
        setSelectedIds([]);
    };

    const close = () => {
        setSelectedIds([]);
        onClose();
    };

    const renderName = (source: ExternalSourceCandidate) => (
        <span title={source.Name}>{source.Name}</span>
    );

    const renderCheckbox = (source: ExternalSourceCandidate) => {
        const disabled = linkedSet.has(source.ID);
        return (
            <input
                type="checkbox"
                checked={selectedSet.has(source.ID)}
                disabled={disabled}
                aria-label={InovxLoader.t('facility:externalAttributes.selectSource')}
                onChange={event => {
                    const checked = event.target.checked;
                    setSelectedIds(current => checked
                        ? [...current, source.ID]
                        : current.filter(id => id !== source.ID));
                }}
                onClick={event => event.stopPropagation()}
            />
        );
    };

    const rowSelecting = (event: RowSelectingEventArgs) => {
        event.cancel = true;
    };

    return (
        <DialogComponent
            visible={visible}
            isModal={true}
            showCloseIcon={true}
            width="860px"
            cssClass="configuration-dialog external-attributes-picker-dialog"
            target="#app"
            zIndex={110000}
            close={close}
            header={InovxLoader.t('facility:externalAttributes.pickerTitle')}
            footerTemplate={() => (
                <div className="external-attributes-dialog-actions">
                    <button type="button" className={`btn btn-primary${addDisabled ? ' disabled' : ''}`} disabled={addDisabled} onClick={addSelected}>
                        {InovxLoader.t('facility:externalAttributes.linkSelected')}
                    </button>
                    <button type="button" className="btn btn-default" onClick={close}>
                        {InovxLoader.t('facility:preferences.cancelButton')}
                    </button>
                </div>
            )}
        >
            <div className="configuration-grid external-attributes-picker-grid">
                <GridComponent
                    dataSource={sources}
                    allowSorting={true}
                    allowPaging={false}
                    height={320}
                    rowSelecting={rowSelecting}
                    emptyRecordTemplate={InovxLoader.t('facility:externalAttributes.noSources')}
                    locale={typeof InovxLoader !== 'undefined' ? InovxLoader.getSyncfusionLocale() : 'en-US'}
                >
                    <ColumnsDirective>
                        <ColumnDirective width="54" allowSorting={false} template={renderCheckbox} />
                        <ColumnDirective field="Name" headerText={InovxLoader.t('facility:externalAttributes.sourceName')} width="34%" template={renderName} />
                        <ColumnDirective field="ProviderName" headerText={InovxLoader.t('facility:externalAttributes.provider')} width="24%" />
                        <ColumnDirective field="SourceObjectName" headerText={InovxLoader.t('facility:externalAttributes.sourceObject')} width="36%" />
                    </ColumnsDirective>
                    <Inject services={[Selection, Sort]} />
                </GridComponent>
            </div>
        </DialogComponent>
    );
};

export default ExternalDataSourcePicker;
